import { config } from '@/config'
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi'
import { base, baseSepolia, mainnet, sepolia } from '@reown/appkit/networks'
import { createAppKit } from '@reown/appkit/react'
import type { ReactNode } from 'react'
import { type Config, WagmiProvider } from 'wagmi'

const APPKIT_PROJECT_ID_FALLBACK = '00000000000000000000000000000000'

let wagmiConfigSingleton: Config | null = null
let appKitSingleton: ReturnType<typeof createAppKit> | null = null

function getNetworkByChainId(chainId: number) {
  switch (chainId) {
    case 1:
      return mainnet
    case 8453:
      return base
    case 11155111:
      return sepolia
    case 84532:
      return baseSepolia
    default:
      return baseSepolia
  }
}

function ensureAppKitSetup(): {
  wagmiConfig: Config
  appKit: ReturnType<typeof createAppKit> | null
} {
  if (wagmiConfigSingleton) {
    return {
      wagmiConfig: wagmiConfigSingleton,
      appKit: appKitSingleton,
    }
  }

  const network = getNetworkByChainId(config.chainId)
  const projectId = config.appKitProjectId || APPKIT_PROJECT_ID_FALLBACK

  const wagmiAdapter = new WagmiAdapter({
    projectId,
    networks: [network],
  })

  wagmiConfigSingleton = wagmiAdapter.wagmiConfig

  if (config.appKitProjectId) {
    appKitSingleton = createAppKit({
      adapters: [wagmiAdapter],
      projectId,
      networks: [network],
      defaultNetwork: network,
      metadata: {
        name: 'WEFA',
        description: 'Plant care and community play',
        url: typeof window === 'undefined' ? 'https://wefa.app' : window.location.origin,
        icons: ['https://wefa.app/icon.png'],
      },
      features: {
        analytics: false,
        email: true,
        socials: ['apple', 'google'],
      },
      enableEIP6963: true,
    })
  } else {
    appKitSingleton = null
  }

  return {
    wagmiConfig: wagmiConfigSingleton,
    appKit: appKitSingleton,
  }
}

export function openWalletModal(): void {
  if (!appKitSingleton) {
    throw new Error('Wallet connect is not configured. Set VITE_APPKIT_PROJECT_ID.')
  }
  appKitSingleton.open()
}

interface AppKitProviderProps {
  children: ReactNode
}

export function AppKitProvider({ children }: AppKitProviderProps) {
  const { wagmiConfig } = ensureAppKitSetup()
  return <WagmiProvider config={wagmiConfig}>{children}</WagmiProvider>
}
